// Copyright (c) Bentley Systems
import {
  Button,
  DefaultCell,
  InformationPanel,
  InformationPanelBody,
  InformationPanelContent,
  InformationPanelHeader,
  InformationPanelWrapper,
  Table,
  Text,
} from "@itwin/itwinui-react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { selectAccessToken } from "../app/authorization-slice";
import {
  getTotalHeightWithMargins,
} from "../Helpers/helper-functions";
import { useNodePermission } from "../Helpers/useNodePermission";
import {
  selectMainMenuHeightWithMargin,
  selectSideMenuWidthWithMargin,
} from "../app/header-slice";
import { DawComponent } from "../Helpers/daw-component";
import {
  SvgProgressBackward,
  SvgUnlink,
} from "@itwin/itwinui-icons-react";
import { Organization } from "./Models/organizations";
import {
  CellRendererProps,
  Column,
  Row,
} from "@itwin/itwinui-react/react-table";
import { OrgSubscription } from "./Models/org-subscription";
import { setConfirmation } from "../app/confirmation-slice";
import SubscribeProjectForm from "./SubscribeProjectForm";
import { useToastNotificationService } from "../Helpers/ToastNotificationService";
import { setformOpen } from "../app/form-open-slice";
import { ODataTextFilter } from "../Helpers/ODataTextFilter";
import { useOrganizationSubscriptions } from "./useOrganizationSubscriptions";

const SubscribedOrganizations = () => {
  const dispatch = useAppDispatch();
  const { showToast } = useToastNotificationService();
  const [panelUrl, setPanelUrl] = useState<string>();
  const sideMenuWidth = useAppSelector(selectSideMenuWidthWithMargin);
  const mainMenuHeight = useAppSelector(selectMainMenuHeightWithMargin);
  const headerDivRef = useRef<HTMLDivElement>(null);
  const headerButtonsDivRef = useRef<HTMLDivElement>(null);

  const footerDivRef = useRef<HTMLDivElement>(null);
  const [footerDivHeight, setFooterDivHeight] = useState<number>(0);
  const [headerDivHeight, setHeaderDivHeight] = useState<number>(0);
  const [headerCbDivHeight, setHeaderCbDivHeight] = useState<number>(0);
  const orgProjHeaderDivRef = useRef<HTMLDivElement>(null);
  const [orgProjHeaderDivHeight, setOrgProjHeaderDivHeight] =
    useState<number>(0);
  const { isNodeAllowed } = useNodePermission(
    DawComponent.DAW_COMPONENT_SUBSCRIBED_ORGANIZATIONS,
  );
  const accessToken = useAppSelector(selectAccessToken);

  const {
    organizations,
    selectedOrganization,
    setSelectedOrganization,
    isOrgLoading,
    orgPageSize,
    orgSortBy,
    orgTableFiltersState,
    handleOrgSort,
    handleOrgFilter,
    orgPaginator,
    organizationSubscriptions,
    setOrganizationSubscriptions,
    isProjLoading,
    projPageSize,
    projSortBy,
    projTableFiltersState,
    handleProjSort,
    handleProjFilter,
    projPaginator,
    fetchSubscriptions,
    unsubscribeProject,
    refresh,
  } = useOrganizationSubscriptions({
    accessToken,
    dispatch,
    isNodeAllowed,
  });

  useEffect(() => {
    if (panelUrl === undefined) {
      dispatch(setformOpen({ isOpen: false }));
    } else {
      dispatch(setformOpen({ isOpen: true }));
    }
  }, [dispatch, panelUrl]);

  useEffect(() => {
    const calculateHeights = () => {
      if (headerDivRef.current) {
        const element = headerDivRef.current;
        const totalHeight = getTotalHeightWithMargins(element);
        setHeaderDivHeight(totalHeight);
      }
      if (headerButtonsDivRef.current) {
        const element = headerButtonsDivRef.current;
        const totalHeight = getTotalHeightWithMargins(element);
        setHeaderCbDivHeight(totalHeight);
      }
      if (footerDivRef.current) {
        const element = footerDivRef.current;
        const totalHeight = getTotalHeightWithMargins(element);
        setFooterDivHeight(totalHeight);
      }
      if (orgProjHeaderDivRef.current) {
        const element = orgProjHeaderDivRef.current;
        const totalHeight = getTotalHeightWithMargins(element);
        setOrgProjHeaderDivHeight(totalHeight);
      }
    };
    // Calculate heights initially
    calculateHeights();

    // Add resize event listener
    window.addEventListener("resize", calculateHeights);

    // Cleanup the event listener on unmount
    return () => {
      window.removeEventListener("resize", calculateHeights);
    };
  }, [selectedOrganization]);

  const allHeadersHeight = mainMenuHeight + headerDivHeight + headerCbDivHeight;

  const orgColumns: Column<Organization>[] = useMemo(() => [
    {
      Header: "Organization Name",
      accessor: (org: Organization) => org.Name,
      id: "Name",
      Filter: ODataTextFilter,
      cellRenderer: (props: CellRendererProps<Organization>) => (
        <DefaultCell
          {...props}
          style={{ cursor: "pointer" }}
          onClick={() => setSelectedOrganization(props.cellProps.row.original)}
        >
          {props.cellProps.value}
        </DefaultCell>
      ),
    },
  ], []);

  const onRowClick = useCallback(
    (event: React.MouseEvent, row: Row<Organization>) =>
      setSelectedOrganization(row.original),
    [],
  );

  const onUnsubscribeProjectClick = useCallback(
    async (orgSub: OrgSubscription) => {
      dispatch(
        setConfirmation({
          isConfirmationOpen: true,
          onOkAction: async () => {
            const unsubscribed = await unsubscribeProject(orgSub);
            if (unsubscribed) {
              showToast({
                status: "positive",
                message: `Project "${orgSub.Project.Name}" unsubscribed successfully!`,
              });
              void fetchSubscriptions(orgSub.OrganizationId, undefined);
            }
          },
          caption: "Unsubscribe confirmation",
          text: "Are you sure you want to unsubscribe this project?",
          okText: "Yes",
          cancelText: "No",
        }),
      );
    },
    [unsubscribeProject, dispatch, showToast, fetchSubscriptions],
  );

  const projColumns: Column<OrgSubscription>[] = useMemo(() => [
    {
      Header: "Project name",
      accessor: (row: OrgSubscription) => row.Project?.Name ?? "",
      id: "projectName",
      minWidth: 200,
      Filter: ODataTextFilter,
    },
    {
      Header: "Unique name",
      accessor: (row: OrgSubscription) => row.Project?.UniqueName ?? "",
      id: "uniqueName",
      minWidth: 200,
      Filter: ODataTextFilter,
    },
    {
      Header: "Version",
      accessor: (row: OrgSubscription) => row.Project?.Version ?? "",
      id: "projectVersion",
      width: 80,
    },
    {
      Header: "Subscribed on",
      accessor: (row: OrgSubscription) =>
        row.SubscribedOn?.toLocaleString?.() ?? String(row.SubscribedOn),
      id: "subscribedOn",
      width: 180,
    },
    {
      Header: "Subscribed by",
      accessor: (row: OrgSubscription) => row.SubscribedByName,
      id: "subscribedByName",
      width: 180,
      Filter: ODataTextFilter,
    },
    {
      Header: "Unsubscribe",
      id: "actions",
      width: 120,
      disableSortBy: true,
      sticky: "right",
      cellRenderer: (props: CellRendererProps<OrgSubscription>) => (
        <DefaultCell
          {...props}
          style={{
            justifyContent: "center",
            alignItems: "center",
            display: "flex",
          }}
        >
          <Button
            size="small"
            styleType="borderless"
            startIcon={<SvgUnlink />}
            aria-label={`Unsubscribe ${props.cellProps.row.original.Project?.Name}`}
            onClick={() => {
              void onUnsubscribeProjectClick(props.cellProps.row.original);
            }}
          />
          <div className="becs-table-cell-shadow-left-elist" />
        </DefaultCell>
      ),
    },
  ], [onUnsubscribeProjectClick]);

  function onPanelClose(refreshList: boolean) {
    setPanelUrl(undefined);

    if (refreshList) {
      refresh();
    }
  }

  return (
    <div style={{ height: "100%" }}>
      <InformationPanelWrapper
        style={{ height: "100%", maxWidth: "100%", borderRadius: 0 }}
      >
        <div>
          {panelUrl !== undefined && <div className="overlay" />}
          <div style={{ display: "flex", justifyContent: "center" }}>
            <div
              style={
                panelUrl !== undefined
                  ? { pointerEvents: "none", opacity: "0.8" }
                  : {}
              }
            >
              <div className="becs-infopanelbody-footer-line">
                <div className="becs-all-margin" ref={headerDivRef}>
                  <div className="rowWrap">
                    <Text variant="title">{"Organizations subscriptions"}</Text>
                  </div>
                </div>
              </div>

              <div className="becs-ltr-margin">
                <div>
                  <div
                    className="horizontal becs-tb-margin"
                    ref={headerButtonsDivRef}
                  >
                    <div className="leftAlign">
                      <div className="rowWrap">
                        <Button
                          key="newSubscriptionBtn"
                          as="button"
                          onClick={() => {
                            setPanelUrl("addProject");
                          }}
                          styleType="high-visibility"
                        >
                          <div>Add subscription</div>
                        </Button>
                      </div>
                    </div>

                  </div>
                </div>
                <div style={{ maxWidth: `calc(100vw - ${sideMenuWidth}px)` }}>
                  <div>
                    <div
                      className="becs-infopanelbody-footer-line"
                      style={{
                        background: "var(--iui-color-background)",
                        height: `calc(100vh - ${allHeadersHeight + footerDivHeight}px)`,
                        width: `calc(100vw - ${sideMenuWidth}px)`,
                      }}
                    >
                      {selectedOrganization === undefined && (
                        <Table
                          style={{
                            height: `calc(100vh - ${allHeadersHeight + footerDivHeight}px)`,
                          }}
                          columns={orgColumns}
                          data={organizations}
                            isLoading={isOrgLoading}
                            emptyTableContent="No organizations."
                            density="extra-condensed"
                            styleType="zebra-rows"
                            isSelectable={true}
                            selectionMode="single"
                            selectRowOnClick={true}
                            getRowId={(row) => row.Id}
                            onRowClick={onRowClick}
                            rowProps={() => ({
                              style: { cursor: "pointer" },
                            })}
                            isSortable={true}
                            manualPagination
                            manualSortBy
                            manualFilters
                            onSort={handleOrgSort}
                            onFilter={handleOrgFilter}
                            initialState={{
                              sortBy: orgSortBy,
                              filters: orgTableFiltersState,
                            }}
                            paginatorRenderer={orgPaginator}
                            pageSize={orgPageSize}
                        />
                      )}
                      {selectedOrganization !== undefined && (
                        <div>
                          <div
                            ref={orgProjHeaderDivRef}
                            className="becs-infopanelbody-footer-line"
                          >
                            <div className="rowWrap">
                              <Button
                                key="backBtn"
                                styleType="borderless"
                                onClick={() => {
                                  setSelectedOrganization(undefined);
                                  setOrganizationSubscriptions([]);
                                }}
                              >
                                <SvgProgressBackward />
                              </Button>
                              <Text variant="title">
                                {selectedOrganization.Name}
                              </Text>
                            </div>
                          </div>

                          <Table
                            style={{
                              height: `calc(100vh - ${allHeadersHeight + footerDivHeight + orgProjHeaderDivHeight}px)`,
                            }}
                            columns={projColumns}
                            data={organizationSubscriptions}
                            isLoading={isProjLoading}
                            emptyTableContent="No subscriptions."
                            density="extra-condensed"
                            styleType="zebra-rows"
                            isSortable={true}
                            manualPagination
                            manualSortBy
                            manualFilters
                            onSort={handleProjSort}
                            onFilter={handleProjFilter}
                            initialState={{
                              sortBy: projSortBy,
                              filters: projTableFiltersState,
                            }}
                            paginatorRenderer={projPaginator}
                            pageSize={projPageSize}
                          />
                        </div>
                      )}
                    </div>
                    <div ref={footerDivRef} className="becs-top-border-line">
                      {/* Both org and proj pagination are handled by paginators inside each Table above */}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {panelUrl !== undefined && (
          <InformationPanel
            resizable={true}
            isOpen={panelUrl !== undefined}
            style={{
              maxWidth: "70%",
              minWidth: "40%",
              zIndex: 2,
            }}
            className="iui-infoPanel"
          >
            <InformationPanelHeader style={{ padding: 0 }}>
              <div className="becs-all-margin">
                <Text
                  variant="title"
                  style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                >
                  {`Subscribe project${selectedOrganization ? ` to ${selectedOrganization?.Name}` : ""}`}
                </Text>
              </div>
            </InformationPanelHeader>
            <InformationPanelBody style={{ padding: 0 }}>
              <InformationPanelContent
                displayStyle="default"
                style={{ padding: 0, height: "100%" }}
              >
                <SubscribeProjectForm
                  orgId={selectedOrganization?.Id}
                  closePanelFunction={onPanelClose}
                />
              </InformationPanelContent>
            </InformationPanelBody>
          </InformationPanel>
        )}
      </InformationPanelWrapper>
    </div>
  );
};

export default SubscribedOrganizations;
